import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Menu } from "primereact/menu";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { customerService } from "../../services/CustomerService";
import SaleService from "../../services/SaleService";
import FinancialAccountService from "../../services/FinancialAccountService";
import SaleReceipt from "../../components/Sale/SaleReceipt";
import CustomerStatementReceipt from "../../components/Customer/CustomerStatementReceipt";

export default function CustomerDetailPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const toast = useRef(null);
  const receiptRef = useRef(null);
  const statementReceiptRef = useRef(null);
  const printMenuRef = useRef(null);
  const activeSaleIdRef = useRef(null);
  const [activeSaleId, setActiveSaleId] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [fullDetail, setFullDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [soldProducts, setSoldProducts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [returns, setReturns] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);

  // Payment Form States
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedReceivableId, setSelectedReceivableId] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentType, setPaymentType] = useState("Nakit");
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentDescription, setPaymentDescription] = useState("");
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);

  // Extra Payment Details
  const [checkDetails, setCheckDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: ""
  });
  const [promissoryDetails, setPromissoryDetails] = useState({
    dueDate: null,
    drawer: "",
    guarantor: ""
  });

  // Adjustment State
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentDescription, setAdjustmentDescription] = useState("");

  // Discount State
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountDescription, setDiscountDescription] = useState("");

  // Edit Payment State
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  const [isExpanded, setIsExpanded] = useState(false);

  // Statement Dialog State
  const [showStatementDialog, setShowStatementDialog] = useState(false);

  // Return Product State
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showReturnPaymentActionDialog, setShowReturnPaymentActionDialog] = useState(false);
  const [isInvoiceReturn, setIsInvoiceReturn] = useState(false);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [selectedReturnRow, setSelectedReturnRow] = useState(null);

  // Sale Detail Popup States
  const filteredAccounts = React.useMemo(() => {
    if (paymentType === "Nakit") return financialAccounts.filter(a => a.accountType === 1);
    if (paymentType === "Havale/EFT") return financialAccounts.filter(a => a.accountType === 2);
    if (paymentType === "Kredi Kartı") return financialAccounts.filter(a => a.accountType === 3);
    return [];
  }, [financialAccounts, paymentType]);

  useEffect(() => {
    if (filteredAccounts.length === 1) {
      setSelectedAccountId(filteredAccounts[0].id);
    } else {
      setSelectedAccountId(null);
    }
  }, [filteredAccounts]);

  const [saleDetailVisible, setSaleDetailVisible] = useState(false);
  const [selectedSaleDetail, setSelectedSaleDetail] = useState(null);
  const [loadingSaleDetail, setLoadingSaleDetail] = useState(false);

  const handlePrintCollection = async (transactionId) => {
    try {
        setLoadingSaleDetail(true);
        const res = await customerService.getPaymentDetail(transactionId);
        if (res.data.success) {
            receiptRef.current.printWithData(res.data.data, 'collection');
        } else {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Tahsilat detayı alınamadı', life: 3000 });
        }
    } catch (err) {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Yazdırma sırasında hata oluştu', life: 3000 });
    } finally {
        setLoadingSaleDetail(false);
    }
  };

  const paymentTypes = [
    { label: "Nakit", value: "Nakit" },
    { label: "Havale/EFT", value: "Havale/EFT" },
    { label: "Kredi Kartı", value: "Kredi Kartı" },
    { label: "Çek", value: "Çek" },
    { label: "Senet", value: "Senet" }
  ];

  /* =====================
     FETCH
  ===================== */
  const fetchCustomerDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await customerService.getCustomerDetail(id);
      const data = res.data.data;

      setFullDetail(data);
      setCustomer(data.customer);
      setSummary(data.summary);
      setSoldProducts(data.soldProducts || []);
      setPayments(data.payments || []);
      setInvoices(data.invoices || []);
      setReturns(data.returns || []);
      setUpcomingPayments(data.upcomingPayments || []);
    } catch (err) {
      console.error("Detay getirilirken hata:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomerDetail();
    FinancialAccountService.getAll().then(res => {
      if (res.success) {
        setFinancialAccounts(res.data || []);
      }
    });
  }, [fetchCustomerDetail]);

  const handleReceivePayment = async () => {
    if (paymentAmount <= 0) return;

    try {
      setSubmitting(true);
      const payload = {
        customerId: parseInt(id),
        amount: paymentAmount,
        paymentType: paymentType,
        paymentDate: paymentDate,
        description: paymentDescription,
        financialAccountId: selectedAccountId,
        customerReceivableDueItemId: selectedReceivableId,
        ...(paymentType === 'Çek' ? checkDetails : {}),
        ...(paymentType === 'Senet' ? promissoryDetails : {})
      };

      const res = await customerService.receivePayment(payload);

      // Reset form
      setPaymentAmount(0);
      setPaymentDescription("");
      setCheckDetails({ bankName: "", bankBranch: "", checkNumber: "", dueDate: null, drawer: "" });
      setPromissoryDetails({ dueDate: null, drawer: "", guarantor: "" });
      setSelectedReceivableId(null);
      setShowPaymentDialog(false);

      // Refresh data
      toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Tahsilat kaydedildi', life: 3000 });
      await fetchCustomerDetail();
      setShowPaymentDialog(false);

      // Printing Choice
      setTimeout(() => {
        confirmDialog({
          message: 'Tahsilat fişi yazdırmak ister misiniz?',
          header: 'Fiş Yazdır',
          icon: 'pi pi-print',
          acceptLabel: 'Evet, Yazdır',
          rejectLabel: 'Hayır',
          accept: async () => {
             // We need the last payment ID. It's usually in the response or we can find it in the refreshed list.
             // Since we just refreshed, let's take the first payment from the list if it matches.
             // Better yet, the backend could return the ID.
             const lastTrId = res.data?.data || res.success || res.data?.success;
             // ServiceResult usually has the value in .data or is the result itself if using a flat response.
             // Based on my Command change, it returns ServiceResult<long>
             if (typeof res.data?.data === 'number' || typeof res.data?.data === 'string') {
                 handlePrintCollection(res.data.data);
             } else if (res.data && (typeof res.data === 'number' || typeof res.data === 'string')) {
                 handlePrintCollection(res.data);
             }
          }
        });
      }, 500);

    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Ödeme alınırken hata oluştu', life: 3000 });
      console.error("Ödeme alınırken hata:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnProduct = (rowData, forcedDeletePayments = null, isFullInvoice = false) => {
    setIsInvoiceReturn(isFullInvoice);
    // Check if the whole invoice will become empty
    const rowProductId = rowData.productId || rowData.ProductId;
    const rowSaleId = rowData.saleId || rowData.SaleId || (isFullInvoice ? rowData.id : null);

    const sameSaleProducts = soldProducts.filter(p => (p.saleId || p.SaleId) === rowSaleId);
    const totalQtyInSale = sameSaleProducts.reduce((sum, p) => sum + p.quantity, 0);
    const willBeEmpty = (totalQtyInSale - returnQuantity) === 0;

    const proceedReturn = async (deletePayments = false, isFullInvoiceParam = isInvoiceReturn) => {
      try {
        setSubmitting(true);
        let res;
        if (isFullInvoiceParam) {
            res = await customerService.createSaleReturn({
                saleId: rowSaleId,
                customerId: parseInt(id),
                description: `Fatura iadesi: ${rowData.saleNo || rowData.id}`,
                deletePayments: deletePayments
            });
        } else {
            res = await customerService.createReturn({
                productId: rowProductId,
                saleId: rowSaleId,
                customerId: parseInt(id),
                quantity: returnQuantity,
                unitPrice: rowData.unitPrice,
                description: `Satıştan iade: ${rowData.saleDate}`,
                deletePayments: forcedDeletePayments !== null ? forcedDeletePayments : deletePayments
            });
        }

        if (res.data.success || res.success) {
            setShowReturnDialog(false);
            setShowReturnPaymentActionDialog(false);
            toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İade işlemi kaydedildi', life: 3000 });
            await fetchCustomerDetail();
        } else {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: res.data?.message || res.message || 'İade alınırken hata oluştu', life: 3000 });
        }
      } catch (err) {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İade alınırken hata oluştu', life: 3000 });
      } finally {
        setSubmitting(false);
        setIsInvoiceReturn(false);
      }
    };

    if (forcedDeletePayments !== null) {
      proceedReturn(forcedDeletePayments, isFullInvoice);
      return;
    }

    if (willBeEmpty || isFullInvoice) {
      const sale = invoices.find(i => i.id === rowSaleId);
      const hasPayments = sale && sale.paidAmount > 0;

      if (hasPayments) {
          setShowReturnPaymentActionDialog(true);
      } else {
          confirmDialog({
              message: isFullInvoice ? 'Faturayı tamamen iade etmek istediğinize emin misiniz?' : 'Faturada ürün kalmayacak onaylıyor musunuz?',
              header: isFullInvoice ? 'Fatura İade Onayı' : 'Kritik İade Onayı',
              icon: 'pi pi-exclamation-triangle',
              acceptClassName: 'p-button-danger',
              acceptLabel: isFullInvoice ? 'Evet, Faturayı İade Et' : 'Evet, Faturayı İptal Et ve İade Al',
              rejectLabel: 'Vazgeç',
              accept: () => proceedReturn(false, isFullInvoice)
          });
      }
    } else {
      confirmDialog({
        message: `${rowData.productName} ürününden ${returnQuantity} adet iade almak istediğinize emin misiniz?`,
        header: 'İade Onayı',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Evet, İade Al',
        rejectLabel: 'Vazgeç',
        accept: () => proceedReturn(false, false)
      });
    }
  };

  const handleApplyDiscount = async () => {
    try {
      setSubmitting(true);
      await customerService.applyDiscount({
        customerId: parseInt(id),
        amount: discountAmount,
        discountDate: new Date(),
        description: discountDescription || "Müşteri İskontosu"
      });
      setShowDiscountDialog(false);
      setDiscountAmount(0);
      setDiscountDescription("");
      toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İskonto uygulandı', life: 3000 });
      await fetchCustomerDetail();
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İskonto uygulanırken hata oluştu', life: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdjustBalance = async () => {
    try {
      setSubmitting(true);
      await customerService.adjustBalance({
        customerId: parseInt(id),
        amount: adjustmentAmount,
        adjustmentDate: new Date(),
        description: adjustmentDescription || "Manuel Bakiye Düzenleme"
      });
      setShowAdjustmentDialog(false);
      setAdjustmentAmount(0);
      setAdjustmentDescription("");
      toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Bakiye düzenlendi', life: 3000 });
      await fetchCustomerDetail();
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Düzenleme yapılırken hata oluştu', life: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = (paymentId) => {
    confirmDialog({
      message: 'Bu işlemi silmek istediğinize emin misiniz?',
      header: 'Silme Onayı',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Evet, Sil',
      rejectLabel: 'Vazgeç',
      accept: async () => {
        try {
          await customerService.deletePayment(paymentId);
          toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İşlem silindi', life: 3000 });
          await fetchCustomerDetail();
        } catch (err) {
          toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İşlem silinirken hata oluştu', life: 3000 });
        }
      }
    });
  };

  const handleShowSaleDetail = async (saleId) => {
    try {
      setLoadingSaleDetail(true);
      setSaleDetailVisible(true);
      const res = await SaleService.getSaleDetail(saleId);
      setSelectedSaleDetail(res);
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Satış detayı getirilemedi', life: 3000 });
    } finally {
      setLoadingSaleDetail(false);
    }
  };

  const handleUpdatePayment = async () => {
    try {
      setSubmitting(true);
      await customerService.updatePayment({
        transactionId: editingPayment.id,
        amount: editingPayment.amount,
        paymentType: editingPayment.paymentType,
        paymentDate: new Date(editingPayment.date.split('.').reverse().join('-')), // simple parse dd.mm.yyyy
        description: editingPayment.description || "",
        financialAccountId: editingPayment.financialAccountId
      });
      setShowEditPaymentDialog(false);
      toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'İşlem güncellendi', life: 3000 });
      await fetchCustomerDetail();
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Güncelleme yapılırken hata oluştu', life: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  /* =====================
     GUARDS
  ===================== */
  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] animate-fade-in">
      <i className="pi pi-spin pi-spinner text-4xl text-blue-600"></i>
      <p className="mt-4 text-slate-600 font-medium">Müşteri bilgileri yükleniyor...</p>
    </div>
  );

  if (!customer) return (
    <div className="p-6 animate-fade-in">
      <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-8 text-center max-w-lg mx-auto">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="pi pi-exclamation-triangle text-red-500 text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Müşteri bulunamadı</h3>
        <p className="text-slate-500">İstediğiniz müşteri kaydı sistemde mevcut olmayabilir.</p>
      </div>
    </div>
  );
const balance = summary.balance;

const balanceType =
  balance < 0 ? "NEGATIVE" :
  balance > 0 ? "POSITIVE" :
  "ZERO";
  const printMenuItems = [
    {
      label: 'Yazdırma Seçenekleri',
      items: [
        {
          label: 'Bilgi Fişi',
          icon: 'pi pi-print',
          command: () => receiptRef.current.print(activeSaleIdRef.current, 'info')
        },
        {
          label: 'Tahsilat Makbuzu',
          icon: 'pi pi-money-bill',
          command: () => receiptRef.current.print(activeSaleIdRef.current, 'collection')
        },
        {
          label: 'Detaylı Bilgi Fişi',
          icon: 'pi pi-file-pdf',
          command: () => receiptRef.current.print(activeSaleIdRef.current, 'detailed')
        }
      ]
    }
  ];

  /* =====================
     RENDER
  ===================== */
  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex items-center gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-info p-button-text hover:bg-blue-50"
          onClick={() => {
            setEditingPayment({ ...rowData });
            setShowEditPaymentDialog(true);
          }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-text hover:bg-red-50"
          onClick={() => handleDeletePayment(rowData.id)}
        />
      </div>
    );
  };

const totalSales = summary?.totalSales || 0;
const totalPaid = summary?.totalPaid || 0;
const collectedAmount = summary?.collectedAmount ?? totalPaid; // ayrı bir alan yoksa totalPaid kullan
 

const collectionRate =
  totalSales > 0 ? Math.min((totalPaid / totalSales) * 100, 100) : 0;

const isDebt = balance > 0;
const isCredit = balance < 0;

const statusLabel = isDebt
  ? "Borç Bakiyesi Var"
  : isCredit
  ? "Fazla Tahsilat / Alacak"
  : "Dengede";

  return (
    <div className="animate-fade-in min-h-screen  ">
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
      <ConfirmDialog />
<style>{`
  .customer-info-expand-enter {
    animation: customerInfoExpandEnter 240ms cubic-bezier(0.22, 1, 0.36, 1);
    transform-origin: top center;
  }

  .customer-info-compact-enter {
    animation: customerInfoCompactEnter 180ms ease-out;
  }

  @keyframes customerInfoExpandEnter {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.985);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes customerInfoCompactEnter {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}</style>
      {/* Ürün İade Dialog */}
      <Dialog
        header={
            <div className="flex items-center gap-3">
                <div className="bg-orange-100 p-2 rounded-lg">
                    <i className="pi pi-refresh text-orange-600 text-xl"></i>
                </div>
                <div>
                    <h3 className="text-lg font-black text-slate-900 m-0">Ödeme Kaydı Bulundu</h3>
                    <p className="text-xs text-slate-500 font-medium m-0">İade sonrası faturaya ait ödemeler için işlem seçin</p>
                </div>
            </div>
        }
        visible={showReturnPaymentActionDialog}
        style={{ width: '450px' }}
        onHide={() => setShowReturnPaymentActionDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
            <div className="flex flex-col gap-2">
                <Button
                    label="Ödemeleri de Sil"
                    icon="pi pi-trash"
                    className="p-button-danger font-black"
                    onClick={() => {
                        setShowReturnPaymentActionDialog(false);
                        handleReturnProduct(selectedReturnRow, true, isInvoiceReturn);
                    }}
                />
                <Button
                    label="Ödemeleri Koru (Müşteri Alacağı Yap)"
                    icon="pi pi-wallet"
                    className="p-button-warning font-black"
                    onClick={() => {
                        setShowReturnPaymentActionDialog(false);
                        handleReturnProduct(selectedReturnRow, false, isInvoiceReturn);
                    }}
                />
                <Button
                    label="Vazgeç / İptal"
                    icon="pi pi-times"
                    className="p-button-text p-button-secondary font-bold"
                    onClick={() => setShowReturnPaymentActionDialog(false)}
                />
            </div>
        )}
      >
          <div className="pt-4 pb-2">
              <div className="bg-orange-50 p-4 rounded-2xl border border-orange-100 mb-4">
                  <p className="text-sm font-bold text-orange-800 m-0">
                      Bu faturaya ait ödeme kayıtları mevcut. Fatura iptal edileceği için ödemelerin akıbetini seçmelisiniz.
                  </p>
              </div>
              <ul className="text-xs text-slate-600 space-y-2 ml-4 list-disc">
                  <li><b>Ödemeleri de Sil:</b> Faturaya bağlı tahsilat kayıtları ve kasa/banka hareketleri geri alınır.</li>
                  <li><b>Ödemeleri Koru:</b> Tahsilat kayıtları silinmez, ödeme faturadan bağımsız hale gelerek müşterinin genel alacağına (artı bakiye) dönüşür.</li>
              </ul>
          </div>
      </Dialog>

      {/* Ürün İade Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-lg">
              <i className="pi pi-refresh text-orange-600 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">Ürün İade Al</h3>
              <p className="text-xs text-slate-500 font-medium m-0">İade edilecek miktar bilgisini girin</p>
            </div>
          </div>
        }
        visible={showReturnDialog}
        style={{ width: '400px' }}
        onHide={() => setShowReturnDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => setShowReturnDialog(false)} />
            <Button label="İadeyi Onayla" icon="pi pi-check" className="!bg-orange-600 border-none font-black" onClick={() => handleReturnProduct(selectedReturnRow)} disabled={!returnQuantity || returnQuantity <= 0} />
          </div>
        )}
      >
        <div className="space-y-4 pt-4">
          {selectedReturnRow && (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 mb-2">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">ÜRÜN</div>
              <div className="text-sm font-bold text-slate-700">{selectedReturnRow.productName}</div>
              <div className="mt-2 flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-500">Maksimum İade Edilebilir:</span>
                <span className="text-xs font-black text-slate-900">{selectedReturnRow.quantity} Adet</span>
              </div>
            </div>
          )}
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İade Adedi</label>
            <InputNumber
              value={returnQuantity}
              onValueChange={(e) => setReturnQuantity(e.value)}
              min={1}
              max={selectedReturnRow?.quantity}
              placeholder="Miktar giriniz..."
              autoFocus
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
      </Dialog>

      {/* UNIFIED HEADER & INFO CARD */}
      <div className="bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden no-print">
        {!isExpanded ?  (
  <div className="customer-info-compact-enter p-2 md:p-3">
    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
      
      {/* Sol alan */}
      <div className="flex items-start sm:items-center gap-3 md:gap-4 min-w-0">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text p-button-secondary !rounded-2xl !h-11 !w-11 bg-white border border-slate-200 shadow-sm hover:bg-slate-50 shrink-0"
          onClick={() => navigate("/customers")}
          tooltip="Müşteri listesine dön"
        />

        <div className="relative shrink-0">
          <div className="w-13 h-13 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <i className="pi pi-user text-white text-xl"></i>
          </div>

          <span
            className={`absolute -right-1 -bottom-1 w-4 h-4 rounded-full border-2 border-white ${
              customer.isActive ? "bg-emerald-500" : "bg-red-500"
            }`}
          ></span>
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-black text-slate-400">
              #{customer.id}
            </span>
            <h1 className="text-lg md:text-xl font-black text-slate-900 m-0 leading-tight truncate max-w-[260px] md:max-w-[420px]">
              {customer.name}
            </h1>

            <Tag
              value={customer.isActive ? "Aktif" : "Pasif"}
              severity={customer.isActive ? "success" : "danger"}
              className="text-[9px] font-black uppercase px-2.5 py-0.5 rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
              <i className="pi pi-phone text-slate-400"></i>
              {customer.phone || "Telefon yok"}
            </span> 

            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 max-w-[220px] truncate">
              <i className="pi pi-envelope text-slate-400"></i>
              {customer.email || "E-posta yok"}
            </span> 
          </div>
        </div>
      </div>

      {/* Orta özet alanı */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 xl:min-w-[520px]">
        <div className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3">
          <div className="text-[11px] font-black text-slate-600 uppercase tracking-widest">
            Toplam Satış
          </div>
          <div className="mt-1 text-sm font-black text-slate-800">
            ₺{totalSales.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3">
          <div className="text-[11px] font-black text-slate-600 uppercase tracking-widest">
            Tahsil Edilen
          </div>
          <div className="mt-1 text-sm font-black text-emerald-600">
            ₺{collectedAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
          </div>
        </div>

        <div
          className={`rounded-2xl px-4 py-3 border ${
            isDebt
              ? "!bg-red-50 border-red-100"
              : isCredit
              ? "!bg-blue-50 border-blue-100"
              : "!bg-emerald-50 border-emerald-100"
          }`}
        >
          <div
            className={`text-[11px] font-black uppercase tracking-widest ${
              isDebt
                ? "!text-red-400"
                : isCredit
                ? "!text-blue-400"
                : "!text-emerald-400"
            }`}
          >
            Güncel Bakiye
          </div>

          <div
            className={`mt-1 text-sm font-black ${
              isDebt
                ? "text-red-600"
                : isCredit
                ? "text-blue-700"
                : "text-emerald-700"
            }`}
          >
            ₺{Math.abs(balance).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Sağ aksiyon alanı */}
      <div className="flex items-center justify-between xl:justify-end gap-2 flex-wrap"> 
        <Button
          label="Tahsilat"
          icon="pi pi-plus-circle"
          className="p-button-sm !rounded-xl font-bold !bg-emerald-600 !border-emerald-600 shadow-md shadow-emerald-500/20"
          onClick={() => setShowPaymentDialog(true)}
        />

        <Button
          label="Yeni Satış"
          icon="pi pi-shopping-cart"
          className="p-button-sm !rounded-xl font-bold !bg-blue-600 !border-blue-600 shadow-md shadow-blue-500/20"
          onClick={() =>
            navigate("/sales/productsale", {
              state: {
                preSelectedCustomer: {
                  id: customer.id,
                  name: customer.name,
                  balance: summary.balance,
                  phone: customer.phone,
                },
              },
            })
          }
        />

        <Button
  type="button"
  onClick={() => setIsExpanded(true)}
  className=" 
    group
    !h-10 !px-3
    !rounded-2xl
    !bg-white
    !border !border-slate-400
    !text-slate-700
    shadow-sm
    hover:!bg-slate-900
    hover:!border-slate-900
    hover:!text-white
    transition-all
    duration-400
  "
>
  <span className="flex items-center gap-2">
    <span className="text-[11px] font-black uppercase tracking-wide">
      Detay
    </span>

    <span
      className="
        w-8 h-6 rounded-xl
        bg-slate-100
        flex items-center justify-center
        group-hover:bg-white/15
        transition-all duration-400
      "
    >
      <i className="pi pi-chevron-down text-[11px] group-hover:translate-y-0.5 transition-transform duration-200"></i>
    </span>
  </span>
</Button>
      </div>
    </div>
  </div>
)   : (
          <div className="customer-info-expand-enter p-4 md:p-5 relative">
            <button
  type="button"
  onClick={() => setIsExpanded(false)}
  className="
    absolute top-4 right-4 z-10
    group
    h-10 px-3
    rounded-2xl
    bg-white/95
    border border-slate-200
    text-slate-700
    shadow-sm
    hover:bg-slate-900
    hover:border-slate-900
    hover:text-white
    transition-all
    duration-200
  "
>
  <span className="flex items-center gap-2">
    <span className="text-[11px] font-black uppercase tracking-wide">
      Daralt
    </span>

    <span
      className="
        w-6 h-6 rounded-xl
        bg-slate-100
        flex items-center justify-center
        group-hover:bg-white/15
        transition-all duration-200
      "
    >
      <i className="pi pi-chevron-up text-[11px] group-hover:-translate-y-0.5 transition-transform duration-200"></i>
    </span>
  </span>
</button>
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-stretch">
              {/* SOL - MÜŞTERİ PROFİL KARTI */}
              <div className="xl:col-span-7 shadow-xl border border-slate-600 shadow-slate-600/50 rounded-3xl flex flex-col">
                <div className="h-full rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden">
                  {/* üst bölüm */}
                  <div className="p-4 md:p-5 bg-slate-400 border-b border-slate-100 bg-gradient-to-r from-slate-100 to-darke-400">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <Button
                        icon="pi pi-arrow-left"
                        label="Geri"
                        className="p-button-text p-button-secondary !rounded-2xl h-11 px-4 bg-white border border-slate-200 shadow-sm hover:bg-slate-50"
                        onClick={() => navigate("/customers")}
                      />

                      <div className="flex items-center gap-2 flex-wrap">
                        <Tag
                          value={customer.isActive ? "Aktif" : "Pasif"}
                          severity={customer.isActive ? "success" : "danger"}
                          className="text-[10px] font-black uppercase px-3 py-1 rounded-lg"
                        />
                        <span className="text-[11px] font-bold text-slate-400">
                          ID: #{customer.id}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-col md:flex-row md:items-center gap-4">
                      <div className="w-20 h-20 rounded-[1.75rem] bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20 shrink-0">
                        <i className="pi pi-user text-white text-3xl"></i>
                      </div>

                      <div className="min-w-0 flex-1">
                        <h1 className="text-2xl md:text-[2rem] font-black text-slate-900 leading-tight m-0 break-words">
                          {customer.name}
                        </h1>
                        <p className="text-sm text-slate-500 font-medium mt-2">
                          Müşteri iletişim bilgileri ve hızlı işlem alanı
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* bilgi kutuları */}
                  <div className="p-4 md:p-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="rounded-[1.25rem] border border-slate-200 bg-slate-100 px-4 py-3">
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                          <i className="pi pi-phone text-blue-500"></i>
                          Telefon
                        </div>
                        <div className="mt-2 text-sm font-bold text-slate-800">
                          {customer.phone || "—"}
                        </div>
                      </div>

                      <div className="rounded-[1.25rem] border border-slate-200 bg-slate-100 px-4 py-3">
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                          <i className="pi pi-envelope text-blue-500"></i>
                          E-Posta
                        </div>
                        <div className="mt-2 text-sm font-bold text-slate-800 break-all">
                          {customer.email || "—"}
                        </div>
                      </div>

                      <div className="rounded-[1.25rem] border border-slate-200 bg-slate-100 px-4 py-3">
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                          <i className="pi pi-money-bill text-blue-500"></i>
                          Açılış Bakiyesi
                        </div>
                        <div className="mt-2 text-sm font-bold text-slate-800">
                          ₺{(customer.openingBalance || 0).toLocaleString("tr-TR", {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </div>

                      <div className="rounded-[1.25rem] border border-slate-200 bg-slate-100 px-4 py-3">
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                          <i className="pi pi-map-marker text-blue-500"></i>
                          Adres
                        </div>
                        <div className="mt-2 text-sm font-bold text-slate-800">
                          {customer.address || "Adres bilgisi tanımlanmamış"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* aksiyon alanı */}
                  <div className="px-4 md:px-5 pb-4 md:pb-5 mt-auto">
                    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <Button
                          label="Tahsilat Al"
                          icon="pi pi-plus-circle"
                          className="!rounded-2xl !h-12 text-sm font-bold !bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 shadow-md shadow-emerald-500/20"
                          onClick={() => setShowPaymentDialog(true)}
                        />

                        <Button
                          label="Yeni Satış"
                          icon="pi pi-shopping-cart"
                          className="!rounded-2xl !h-12 text-sm font-bold !bg-blue-600 !border-blue-600 hover:!bg-blue-700 shadow-md shadow-blue-500/20"
                          onClick={() =>
                            navigate("/sales/productsale", {
                              state: {
                                preSelectedCustomer: {
                                  id: customer.id,
                                  name: customer.name,
                                  balance: summary.balance,
                                  phone: customer.phone,
                                },
                              },
                            })
                          }
                        />

                        <Button
                          label="Düzenle"
                          icon="pi pi-user-edit"
                          className="p-button-outlined !rounded-2xl !h-12 text-white text-sm font-bold bg-amber-500 border-amber-300 text-slate-700 hover:bg-amber-600"
                          onClick={() => navigate(`/customers/edit/${id}`)}
                        />

                        <Button
                          label="Bakiye Düzenle"
                          icon="pi pi-sliders-h"
                          className="p-button-outlined !rounded-2xl !h-12 text-sm font-bold border-slate-300 text-slate-700 hover:bg-slate-300"
                          onClick={() => setShowAdjustmentDialog(true)}
                        />

                        <Button
                          label="İskonto Yap"
                          icon="pi pi-percentage"
                          className="p-button-outlined !rounded-2xl !h-12 text-sm font-bold border-rose-300 text-rose-700 hover:bg-rose-50"
                          onClick={() => setShowDiscountDialog(true)}
                        />

                        <Button
                          label="Ekstre Fişi"
                          icon="pi pi-file-pdf"
                          className="!rounded-2xl !h-12 text-sm font-bold !bg-slate-700 !border-slate-700 hover:!bg-slate-800 shadow-md"
                          onClick={() => setShowStatementDialog(true)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SAĞ - NET DURUM KARTI */}
              <div className="xl:col-span-5">
                <div
                  className={`h-full rounded-[2rem] p-5 md:p-6 border relative overflow-hidden shadow-sm ${
                    isDebt
                      ? "bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 border-slate-800 text-white"
                      : "bg-gradient-to-br from-white via-white to-slate-50 border-slate-200 text-slate-900"
                  }`}
                >
          <div
            className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl ${
              isDebt ? "bg-emerald-500/10" : "bg-emerald-100/70"
            }`}
          ></div>

          <div className="relative z-10 h-full flex flex-col justify-between gap-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
  <div
    className={`w-2 h-12 rounded-2xl flex items-center justify-center ${
      isDebt ? "bg-white/10" : "bg-slate-100"
    }`}
  >
    <i
      className={`pi pi-wallet text-lg ${
        isDebt ? "text-white" : "text-slate-700"
      }`}
    ></i>
  </div>

  <div className="flex-1 min-w-0">
    <div
      className={`text-[11px] font-black uppercase tracking-[0.16em] ${
        isDebt ? "text-white/60" : "text-slate-500"
      }`}
    >
      Net Durum
    </div>
    <div
      className={`text-sm font-semibold mt-1 whitespace-nowrap ${
        isDebt ? "text-white/75" : "text-slate-600"
      }`}
    >
      {statusLabel}
    </div>
  </div>
</div>

              <span
                className={`px-6 py-4 rounded-full text-[12px] font-black uppercase tracking-[0.12em] ${
                  isDebt
                    ? "bg-amber-400/15 text-amber-300"
                    : isCredit
                    ? "bg-blue-50 text-blue-700 border border-blue-100"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                }`}
              >
                {isDebt ? "Borç" : isCredit ? "Alacak" : "Dengede"}
              </span>
            </div>

            <div>
              <div
                className={`text-[14px] font-semibold mb-2 ${
                  isDebt ? "text-white/55" : "text-slate-500"
                }`}
              >
                Güncel Bakiye
              </div>

              <div
                className={`text-3xl md:text-[2.6rem] leading-none font-black tracking-tight ${
                  isDebt ? "text-white" : "text-slate-900"
                }`}
              >
                ₺{Math.abs(balance).toLocaleString("tr-TR", {
                  minimumFractionDigits: 2,
                })}
              </div>

              <p
                className={`mt-3 text-[13px] ${
                  isDebt ? "text-white/55" : "text-slate-500"
                }`}
              >
                {isDebt
                  ? "Tahsil edilmesi gereken güncel müşteri bakiyesi."
                  : isCredit
                  ? "Müşteri tarafında fazla tahsilat / alacak durumu bulunuyor."
                  : "Müşterinin hesabı şu anda dengede görünüyor."}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Iskonto Alanları */}
              <div
                className={`rounded-2xl px-4 py-4 ${
                  isDebt
                    ? "bg-white/5 border border-white/10"
                    : "bg-slate-50 border border-slate-200"
                }`}
              >
                <div
                  className={`text-[10px] font-black uppercase tracking-[0.12em] ${
                    isDebt ? "text-white/45" : "text-slate-400"
                  }`}
                >
                  Son Yapılan İskonto
                </div>
                <div
                  className={`mt-2 text-base font-black ${
                    isDebt ? "text-white" : "text-slate-900"
                  }`}
                >
                  ₺{(summary?.lastDiscount || 0).toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>

              <div
                className={`rounded-2xl px-4 py-4 ${
                  isDebt
                    ? "bg-white/5 border border-white/10"
                    : "bg-slate-50 border border-slate-200"
                }`}
              >
                <div
                  className={`text-[10px] font-black uppercase tracking-[0.12em] ${
                    isDebt ? "text-white/45" : "text-slate-400"
                  }`}
                >
                  Toplam Yapılan İskonto
                </div>
                <div
                  className={`mt-2 text-base font-black ${
                    isDebt ? "text-white" : "text-slate-900"
                  }`}
                >
                  ₺{(summary?.totalDiscount || 0).toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>

              <div
                className={`rounded-2xl px-4 py-4 ${
                  isDebt
                    ? "bg-white/5 border border-white/10"
                    : "bg-slate-50 border border-slate-200"
                }`}
              >
                <div
                  className={`text-[10px] font-black uppercase tracking-[0.12em] ${
                    isDebt ? "text-white/45" : "text-slate-400"
                  }`}
                >
                  Tahsil Edilen
                </div>
                <div
                  className={`mt-2 text-base font-black ${
                    isDebt ? "text-white" : "text-slate-900"
                  }`}
                >
                  ₺{collectedAmount.toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>

              <div
                className={`rounded-2xl px-4 py-4 ${
                  isDebt
                    ? "bg-white/5 border border-white/10"
                    : "bg-slate-50 border border-slate-200"
                }`}
              >
                <div
                  className={`text-[10px] font-black uppercase tracking-[0.12em] ${
                    isDebt ? "text-white/45" : "text-slate-400"
                  }`}
                >
                  Toplam Satış
                </div>
                <div
                  className={`mt-2 text-base font-black ${
                    isDebt ? "text-white" : "text-slate-900"
                  }`}
                >
                  ₺{totalSales.toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div> 
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`text-[11px] font-bold ${
                    isDebt ? "text-white/55" : "text-slate-500"
                  }`}
                >
                  Tahsilat İlerlemesi
                </span>
                <span
                  className={`text-[11px] font-black ${
                    isDebt ? "text-white/80" : "text-slate-700"
                  }`}
                >
                  %{collectionRate.toFixed(0)}
                </span>
              </div>

              <div
                className={`h-2.5 rounded-full overflow-hidden ${
                  isDebt ? "bg-white/10" : "bg-slate-100"
                }`}
              >
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500"
                  style={{ width: `${collectionRate}%` }}
                ></div>
              </div>
            </div>

          </div>
        </div>
      </div>

    </div>
  </div>
        )}
</div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6 no-print">
        {/* Sol Taraf: Alınan Ürünler ve Faturalar */}
        <div className="lg:col-span-8 bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden">
          <TabView className="customer-detail-tabs">
            <TabPanel header="Alınan Ürünler" leftIcon="pi pi-box mr-2">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-600 w-1.5 h-5 rounded-full"></div>
                  <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Alınan Ürünler</h2>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      inputId="showDetails"
                      onChange={e => setShowProductDetails(e.checked)}
                      checked={showProductDetails}
                      className="p-checkbox-sm"
                    />
                    <label htmlFor="showDetails" className="text-[11px] font-bold text-slate-600 cursor-pointer">Detayları Göster</label>
                  </div>
                  <Tag value={`${soldProducts.length} KALEM`} className="bg-slate-900 text-[10px] font-black" />
                </div>
              </div>
              <DataTable
                value={soldProducts}
                rows={25}
                paginator
                responsiveLayout="scroll"
                emptyMessage="Henüz ürün satışı yapılmamış."
                className="p-datatable-sm"
                paginatorClassName="border-t border-slate-100 px-4 py-2 bg-slate-50"
                rowClassName={() => 'hover:bg-blue-50/20 transition-colors'}
              >
                <Column field="saleDate" header="TARİH" sortable className="text-slate-500 text-[11px] font-bold" />
                <Column field="productName" header="ÜRÜN ADI" sortable className="text-slate-800 font-black text-xs" />
                <Column field="quantity" header="ADET" sortable body={(rowData) => <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md font-black text-[10px] border border-slate-200">{rowData.quantity}</span>} />
                {showProductDetails && (
                  <Column field="unitPrice" header="B. FİYAT" body={(rowData) => <span className="text-slate-600 text-xs font-bold">₺{rowData.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} />
                )}
                {showProductDetails && (
                  <Column field="totalAmount" header="TOPLAM" body={(rowData) => <span className="text-slate-900 text-xs font-black">₺{rowData.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} />
                )}
                <Column field="description" header="AÇIKLAMA" className="text-slate-500 text-[11px]" />
                <Column
                  header="İADE"
                  body={(rowData) => (
                    <Button
                      icon="pi pi-refresh"
                      className="p-button-rounded p-button-warning p-button-text hover:bg-orange-50"
                      onClick={() => {
                        setSelectedReturnRow(rowData);
                        setReturnQuantity(1);
                        setShowReturnDialog(true);
                      }}
                      tooltip="İade Al"
                      tooltipOptions={{ position: 'top' }}
                    />
                  )}
                  className="w-16"
                />
              </DataTable>
            </TabPanel>

            <TabPanel header="İadeler" leftIcon="pi pi-refresh mr-2">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-600 w-1.5 h-5 rounded-full"></div>
                  <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Alınan İadeler</h2>
                </div>
                <Tag value={`${returns.length} İADE`} className="bg-orange-900 text-[10px] font-black" />
              </div>
              <DataTable
                value={returns}
                rows={25}
                paginator
                responsiveLayout="scroll"
                emptyMessage="İade kaydı bulunamadı."
                className="p-datatable-sm"
                paginatorClassName="border-t border-slate-100 px-4 py-2 bg-slate-50"
                rowClassName={() => 'hover:bg-orange-50/20 transition-colors'}
              >
                <Column field="returnDate" header="TARİH" sortable body={(rowData) => new Date(rowData.returnDate).toLocaleDateString('tr-TR')} className="text-slate-500 text-[11px] font-bold" />
                <Column field="productName" header="ÜRÜN ADI" sortable className="text-slate-800 font-black text-xs" />
                <Column field="quantity" header="ADET" sortable body={(rowData) => <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded-md font-black text-[10px] border border-orange-200">{rowData.quantity}</span>} />
                <Column field="unitPrice" header="B. FİYAT" body={(rowData) => <span className="text-slate-600 text-xs font-bold">₺{rowData.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} />
                <Column field="totalAmount" header="TOPLAM" body={(rowData) => <span className="text-slate-900 text-xs font-black">₺{rowData.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} />
                <Column field="description" header="AÇIKLAMA" className="text-slate-500 text-[11px]" />
              </DataTable>
            </TabPanel>

            <TabPanel header="Faturalar" leftIcon="pi pi-file mr-2">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-600 w-1.5 h-5 rounded-full"></div>
                  <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Satış Faturaları</h2>
                </div>
                <Tag value={`${invoices.length} FATURA`} className="bg-indigo-900 text-[10px] font-black" />
              </div>
              <DataTable
                value={invoices}
                rows={25}
                paginator
                responsiveLayout="scroll"
                emptyMessage="Fatura kaydı bulunamadı."
                className="p-datatable-sm"
                paginatorClassName="border-t border-slate-100 px-4 py-2 bg-slate-50"
                rowClassName={() => 'hover:bg-indigo-50/20 transition-colors'}
              >
                <Column field="saleNo" header="FATURA NO" sortable className="text-slate-800 font-black text-xs" />
                <Column field="saleDate" header="TARİH" sortable className="text-slate-500 text-[11px] font-bold" />
                <Column field="totalQuantity" header="ÜRÜN ADEDİ" sortable body={(rowData) => <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md font-black text-[10px] border border-slate-200">{rowData.totalQuantity}</span>} />
                <Column field="totalAmount" header="TOPLAM TUTAR" body={(rowData) => <span className="text-slate-900 text-xs font-black">₺{rowData.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} sortable />
                <Column field="paidAmount" header="ÖDENEN" body={(rowData) => <span className="text-emerald-600 text-xs font-black">₺{rowData.paidAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} sortable />
                <Column field="dueDate" header="VADE TARİHİ" body={(rowData) => rowData.dueDate ? <span className="text-orange-600 text-xs font-black">{rowData.dueDate}</span> : <span className="text-slate-300">-</span>} sortable />
                <Column
                  header="İŞLEMLER"
                  className="w-40"
                  body={(rowData) => (
                      <div className="flex items-center gap-1">
                        {rowData.remainingAmount > 0.01 && (
                          <Button
                            icon="pi pi-plus-circle"
                            className="p-button-text p-button-success hover:bg-emerald-50 rounded-xl w-8 h-8 p-0"
                            tooltip="Tahsilat Al"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                              setSelectedReceivableId(rowData.receivableDueItemId);
                              setPaymentAmount(rowData.remainingAmount);
                              setPaymentDescription(`${rowData.saleNo} nolu faturaya istinaden tahsilat`);
                              setShowPaymentDialog(true);
                            }}
                          />
                        )}
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-rounded p-button-text p-button-warning hover:bg-orange-50 w-8 h-8 p-0"
                            tooltip="Faturayı İade Et"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                                setSelectedReturnRow(rowData);
                                handleReturnProduct(rowData, null, true);
                            }}
                        />
                        <Button
                          icon="pi pi-print"
                          className="p-button-text p-button-success hover:bg-emerald-50 rounded-xl w-8 h-8 p-0"
                          tooltip="Yazdır"
                          tooltipOptions={{ position: 'top' }}
                          onClick={(e) => {
                            activeSaleIdRef.current = rowData.id;
                            setActiveSaleId(rowData.id);
                            printMenuRef.current.toggle(e);
                          }}
                        />
                        <Button
                          icon="pi pi-search"
                          className="p-button-rounded p-button-text p-button-secondary hover:bg-slate-100 w-8 h-8 p-0"
                          tooltip="Detay"
                          tooltipOptions={{ position: 'top' }}
                          onClick={() => handleShowSaleDetail(rowData.id)}
                        />
                        <Button
                          icon="pi pi-pencil"
                          className="p-button-rounded p-button-text p-button-info hover:bg-blue-50 w-8 h-8 p-0"
                          tooltip="Düzenle"
                          tooltipOptions={{ position: 'top' }}
                          onClick={() => navigate(`/sales/edit/${rowData.id}`)}
                        />
                      </div>
                    )}
                />
              </DataTable>
            </TabPanel>
          </TabView>
        </div>

        {/* Sağ Taraf: Yaklaşanlar ve Tahsilat Geçmişi */}
        <div className="lg:col-span-4 bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden flex flex-col h-full">
          <TabView className="customer-detail-tabs flex-1 flex flex-col">
            <TabPanel header="Yaklaşanlar" leftIcon="pi pi-calendar-clock mr-2">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                 <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-widest m-0">Bekleyen Alacaklar</h2>
                 <Tag value={`${upcomingPayments.length} KALEM`} className="bg-orange-600 text-[9px] font-black" />
              </div>
              <DataTable
                value={upcomingPayments}
                rows={10}
                paginator
                responsiveLayout="scroll"
                emptyMessage="Bekleyen tahsilat bulunmuyor."
                className="p-datatable-sm"
                paginatorClassName="border-t border-slate-100 px-4 py-1 bg-slate-50"
                rowClassName={() => 'hover:bg-orange-50/20 transition-colors'}
              >
                <Column field="dueDate" header="VADE" body={(r) => <span className="font-bold text-slate-700 text-[11px]">{r.dueDate}</span>} />
                <Column field="remainingAmount" header="KALAN" body={(r) => <span className="font-black text-orange-600 text-xs">₺{r.remainingAmount.toLocaleString("tr-TR")}</span>} />
                <Column field="description" header="AÇIKLAMA" body={(r) => <span className="text-[10px] text-slate-400">{r.description}</span>} />
              </DataTable>
            </TabPanel>

            <TabPanel header="Geçmiş" leftIcon="pi pi-history mr-2">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-widest m-0">Tahsilat Geçmişi</h2>
                <Tag value={`${payments.length} İŞLEM`} className="bg-emerald-600 text-[9px] font-black" />
              </div>
              <DataTable
                value={payments}
                rows={25}
                paginator
                responsiveLayout="scroll"
                emptyMessage="Tahsilat kaydı bulunamadı."
                className="p-datatable-sm"
                paginatorClassName="border-t border-slate-100 px-4 py-1 bg-slate-50"
                rowClassName={() => 'hover:bg-emerald-50/20 transition-colors'}
              >
                <Column field="date" header="TARİH" sortable className="text-slate-500 text-[10px] font-bold" />
                <Column field="paymentType" header="YÖNTEM" sortable body={(rowData) => (
                  <div className="flex flex-col">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase w-fit ${rowData.paymentType === 'Bakiye Düzeltme' ? '!bg-orange-100 text-orange-700' : '!bg-emerald-100 text-emerald-700'}`}>
                      {rowData.paymentType}
                      </span>
                      {rowData.paymentType === "Tahsilat" && rowData.description && (
                          <span className="text-[8px] text-slate-400 font-bold mt-0.5 italic">{rowData.description.split('\n')[0]}</span>
                      )}
                  </div>
                )} />
                <Column field="amount" header="TUTAR" body={(rowData) => <span className={`font-black text-xs ${rowData.amount < 0 ? 'text-red-600' : 'text-slate-900'}`}>₺{rowData.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>} sortable />
                <Column header="AKS" body={(rowData) => (
                    <div className="flex items-center gap-1">
                        {rowData.paymentType === "Tahsilat" && (
                            <Button
                                icon="pi pi-print"
                                className="p-button-rounded p-button-success p-button-text hover:bg-emerald-50 w-8 h-8 p-0"
                                onClick={() => handlePrintCollection(rowData.id)}
                                tooltip="Yazdır"
                                tooltipOptions={{ position: 'top' }}
                            />
                        )}
                        {actionBodyTemplate(rowData)}
                    </div>
                )} className="w-20" />
              </DataTable>
            </TabPanel>
          </TabView>
        </div>
      </div>

      {/* Bakiye Düzenleme Dialog */}
      {/* Tahsilat Al Dialog */}
      <Dialog
        header={
            <div className="flex items-center gap-3">
                <div className="!bg-emerald-100 p-2 rounded-lg">
                    <i className="pi pi-plus-circle text-emerald-600 text-xl"></i>
                </div>
                <div>
                    <h3 className="text-lg font-black text-slate-900 m-0">Tahsilat Al</h3>
                    <p className="text-xs text-slate-500 font-medium m-0">Müşteriden ödeme girişi yapın</p>
                </div>
            </div>
        }
        visible={showPaymentDialog}
        style={{ width: '450px' }}
        breakpoints={{ '960px': '85vw', '641px': '95vw' }}
        onHide={() => setShowPaymentDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        blockScroll={true}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => setShowPaymentDialog(false)} />
            <Button label="Tahsilat Kaydet" icon="pi pi-check" className="!bg-emerald-600 border-none font-black" onClick={handleReceivePayment} loading={submitting} disabled={paymentAmount <= 0} />
          </div>
        )}
      >
        <div className="space-y-4 pt-6 pb-6">
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Ödeme Tutarı (₺)</label>
            <InputNumber
              value={paymentAmount}
              onValueChange={(e) => setPaymentAmount(e.value)}
              mode="decimal"
              minFractionDigits={2}
              placeholder="0,00"
              autoFocus
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 pb-5">
            <div className="field col-span-2">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Ödeme Yöntemi</label>
                <Dropdown
                  value={paymentType}
                  options={paymentTypes}
                  onChange={(e) => setPaymentType(e.value)}
                  placeholder="Seçiniz"
                  className="rounded-xl border-slate-200"
                  appendTo="self"
                />
              </div>
              {filteredAccounts.length > 0 && (
                <div className="field ">
                    <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İşlem Yapılacak Hesap</label>
                    <Dropdown
                        value={selectedAccountId}
                        options={filteredAccounts}
                        optionLabel="name"
                        optionValue="id"
                        onChange={(e) => setSelectedAccountId(e.value)}
                        placeholder="Hesap seçin..."
                        className="rounded-xl border-slate-200"
                        appendTo={"self"}
                    />
                </div>
              )}
              
              <div className="field">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İşlem Tarihi</label>
                <Calendar
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.value)}
                  dateFormat="dd.mm.yy"
                  locale="tr"
                  showIcon
                  appendTo={document.body}
                  className="w-full"
                  inputClassName="rounded-xl border-slate-200 py-2 font-bold"
                />
              </div>
          </div>

          {paymentType === "Çek" && (
              <div className="!bg-blue-50/50 p-4 rounded-2xl border border-blue-100 space-y-3 animate-fade-in">
                  <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest block mb-1">ÇEK BİLGİLERİ</span>
                  <div className="grid grid-cols-2 gap-3">
                      <div className="field">
                          <label className="text-[10px] font-bold text-slate-600">Banka</label>
                          <InputText value={checkDetails.bankName} onChange={e => setCheckDetails({...checkDetails, bankName: e.target.value})} className="p-inputtext-sm" />
                      </div>
                      <div className="field">
                          <label className="text-[10px] font-bold text-slate-600">Şube</label>
                          <InputText value={checkDetails.bankBranch} onChange={e => setCheckDetails({...checkDetails, bankBranch: e.target.value})} className="p-inputtext-sm" />
                      </div>
                  </div>
                  <div className="field">
                      <label className="text-[10px] font-bold text-slate-600">Çek Numarası</label>
                      <InputText value={checkDetails.checkNumber} onChange={e => setCheckDetails({...checkDetails, checkNumber: e.target.value})} className="p-inputtext-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                      <div className="field">
                          <label className="text-[10px] font-bold text-slate-600">Vade Tarihi</label>
                          <Calendar value={checkDetails.dueDate} onChange={e => setCheckDetails({...checkDetails, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={document.body} className="p-inputtext-sm" />
                      </div>
                      <div className="field">
                          <label className="text-[10px] font-bold text-slate-600">Keşideci</label>
                          <InputText value={checkDetails.drawer} onChange={e => setCheckDetails({...checkDetails, drawer: e.target.value})} className="p-inputtext-sm" />
                      </div>
                  </div>
              </div>
          )}

          {paymentType === "Senet" && (
              <div className="bg-orange-50/50 p-4 rounded-2xl border border-orange-100 space-y-3 animate-fade-in">
                  <span className="text-[10px] font-black text-orange-600 uppercase tracking-widest block mb-1">SENET BİLGİLERİ</span>
                  <div className="field">
                      <label className="text-[10px] font-bold text-slate-600">Vade Tarihi</label>
                      <Calendar value={promissoryDetails.dueDate} onChange={e => setPromissoryDetails({...promissoryDetails, dueDate: e.value})} dateFormat="dd.mm.yy" locale="tr" showIcon appendTo={document.body} className="p-inputtext-sm" />
                  </div>
                  <div className="field">
                      <label className="text-[10px] font-bold text-slate-600">Borçlu (Düzenleyen)</label>
                      <InputText value={promissoryDetails.drawer} onChange={e => setPromissoryDetails({...promissoryDetails, drawer: e.target.value})} className="p-inputtext-sm" />
                  </div>
                  <div className="field">
                      <label className="text-[10px] font-bold text-slate-600">Kefil</label>
                      <InputText value={promissoryDetails.guarantor} onChange={e => setPromissoryDetails({...promissoryDetails, guarantor: e.target.value})} className="p-inputtext-sm" />
                  </div>
              </div>
          )}

          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Açıklama</label>
            <InputText
              value={paymentDescription}
              onChange={(e) => setPaymentDescription(e.target.value)}
              placeholder="Ek notlar..."
              className="rounded-xl border-slate-200"
            />
          </div>
        </div>
      </Dialog>

      {/* Ekstre Seçim Dialog */}
      <Dialog
        header={
            <div className="flex items-center gap-3">
                <div className="bg-slate-100 p-2 rounded-lg">
                    <i className="pi pi-file-pdf text-slate-600 text-xl"></i>
                </div>
                <div>
                    <h3 className="text-lg font-black text-slate-900 m-0">Ekstre Fişi Çıkart</h3>
                    <p className="text-xs text-slate-500 font-medium m-0">Yazdırmak istediğiniz ekstre tipini seçin</p>
                </div>
            </div>
        }
        visible={showStatementDialog}
        style={{ width: '400px' }}
        onHide={() => setShowStatementDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
            <div className="flex flex-col gap-2 pt-2">
                <Button
                    label="Sade Ekstre (Özet)"
                    icon="pi pi-file"
                    className="!bg-blue-600 border-none font-black !py-3 !rounded-xl"
                    onClick={() => {
                        setShowStatementDialog(false);
                        statementReceiptRef.current.print(fullDetail, 'statement_simple');
                    }}
                />
                <Button
                    label="Detaylı Ekstre (Tüm Liste)"
                    icon="pi pi-list"
                    className="!bg-slate-800 border-none font-black !py-3 !rounded-xl"
                    onClick={() => {
                        setShowStatementDialog(false);
                        statementReceiptRef.current.print(fullDetail, 'statement_detailed');
                    }}
                />
                <Button
                    label="Vazgeç"
                    icon="pi pi-times"
                    className="p-button-text p-button-secondary font-bold"
                    onClick={() => setShowStatementDialog(false)}
                />
            </div>
        )}
      >
          <div className="py-4">
              <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                  <p className="text-sm font-bold text-blue-800 m-0">
                      Müşteriye verilecek hesap ekstresi formatını seçin.
                  </p>
              </div>
          </div>
      </Dialog>

      {/* İskonto Yap Dialog */}
      <Dialog
        header={
            <div className="flex items-center gap-3">
                <div className="bg-rose-100 p-2 rounded-lg">
                    <i className="pi pi-percentage text-rose-600 text-xl"></i>
                </div>
                <div>
                    <h3 className="text-lg font-black text-slate-900 m-0">İskonto Yap</h3>
                    <p className="text-xs text-slate-500 font-medium m-0">Müşterinin güncel borcuna iskonto uygulayın</p>
                </div>
            </div>
        }
        visible={showDiscountDialog}
        style={{ width: '400px' }}
        onHide={() => setShowDiscountDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => setShowDiscountDialog(false)} />
            <Button label="İskonto Uygula" icon="pi pi-check" className="!bg-rose-600 border-none font-black" onClick={handleApplyDiscount} loading={submitting} disabled={discountAmount <= 0} />
          </div>
        )}
      >
        <div className="space-y-4 pt-4">
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İskonto Tutarı (₺)</label>
            <InputNumber
              value={discountAmount}
              onValueChange={(e) => setDiscountAmount(e.value)}
              mode="decimal"
              minFractionDigits={2}
              placeholder="0,00"
              autoFocus
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-rose-500"
            />
          </div>
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">Açıklama</label>
            <InputText
              value={discountDescription}
              onChange={(e) => setDiscountDescription(e.target.value)}
              placeholder="İskonto sebebi..."
              className="rounded-xl border-slate-200"
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Manuel Bakiye Düzenleme"
        visible={showAdjustmentDialog}
        style={{ width: '400px' }}
        onHide={() => setShowAdjustmentDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text" onClick={() => setShowAdjustmentDialog(false)} />
            <Button label="Kaydet" icon="pi pi-check" className="p-button-primary" onClick={handleAdjustBalance} loading={submitting} />
          </div>
        )}
      >
        <div className="space-y-4 pt-2">
          <p className="text-xs text-slate-500 font-medium">Bu işlem müşterinin güncel bakiyesine doğrudan ekleme veya çıkarma yapar. (+ değerler borç ekler, - değerler alacak ekler)</p>
          <div className="field">
            <label className="text-xs font-bold text-slate-600">Düzenleme Tutarı (₺)</label>
            <InputNumber value={adjustmentAmount} onValueChange={e => setAdjustmentAmount(e.value)} mode="decimal" minFractionDigits={2} autoFocus />
          </div>
          <div className="field">
            <label className="text-xs font-bold text-slate-600">Açıklama</label>
            <InputText value={adjustmentDescription} onChange={e => setAdjustmentDescription(e.target.value)} placeholder="Örn: Açılış Bakiyesi" />
          </div>
        </div>
      </Dialog>

      {/* Ödeme Düzenleme Dialog */}
      <Dialog
        header="İşlem Düzenle"
        visible={showEditPaymentDialog}
        style={{ width: '400px' }}
        onHide={() => setShowEditPaymentDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text" onClick={() => setShowEditPaymentDialog(false)} />
            <Button label="Güncelle" icon="pi pi-check" className="p-button-primary" onClick={handleUpdatePayment} loading={submitting} />
          </div>
        )}
      >
        {editingPayment && (
          <div className="space-y-4 pt-2">
            <div className="field">
              <label className="text-xs font-bold text-slate-600">Tutar (₺)</label>
              <InputNumber value={editingPayment.amount} onValueChange={e => setEditingPayment({...editingPayment, amount: e.value})} mode="decimal" minFractionDigits={2} />
            </div>
            <div className="field">
              <label className="text-xs font-bold text-slate-600">Ödeme Yöntemi</label>
              <Dropdown
                value={editingPayment.paymentType}
                options={paymentTypes}
                onChange={e => setEditingPayment({...editingPayment, paymentType: e.value})}
                placeholder="Yöntem Seç"
                appendTo="self"
              />
            </div>
            {filteredAccounts.length > 0 && (
                <div className="field">
                    <label className="text-xs font-bold text-slate-600">İşlem Yapılacak Hesap</label>
                    <Dropdown
                        value={editingPayment.financialAccountId}
                        options={filteredAccounts}
                        optionLabel="name"
                        optionValue="id"
                        onChange={(e) => setEditingPayment({...editingPayment, financialAccountId: e.value})}
                        placeholder="Hesap seçin..."
                        className="rounded-xl border-slate-200"
                    />
                </div>
            )}
            <div className="field">
              <label className="text-xs font-bold text-slate-600">Açıklama</label>
              <InputText value={editingPayment.description || ""} onChange={e => setEditingPayment({...editingPayment, description: e.target.value})} />
            </div>
          </div>
        )}
      </Dialog>

      {/* Satış Detay Popup */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <i className="pi pi-file text-blue-600 text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 m-0">Satış Detayı</h3>
              <p className="text-xs text-slate-500 font-medium m-0">
                {selectedSaleDetail ? `Fatura No: ${selectedSaleDetail.saleNo}` : 'Yükleniyor...'}
              </p>
            </div>
          </div>
        }
        visible={saleDetailVisible}
        style={{ width: '700px' }}
        onHide={() => { setSaleDetailVisible(false); setSelectedSaleDetail(null); }}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
      >
        {loadingSaleDetail ? (
          <div className="flex flex-col items-center justify-center py-10">
            <i className="pi pi-spin pi-spinner text-3xl text-blue-600"></i>
            <p className="mt-2 text-slate-500 font-bold">Detaylar yükleniyor...</p>
          </div>
        ) : selectedSaleDetail && (
          <div className="space-y-6 pt-4">
            {/* Ürünler Tablosu */}
            <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
                <DataTable value={selectedSaleDetail.items} size="small" className="p-datatable-sm">
                    <Column field="productName" header="ÜRÜN ADI" className="font-bold text-xs" />
                    <Column field="quantity" header="ADET" body={(row) => <span className="font-black">{row.quantity}</span>} />
                    <Column field="unitPrice" header="B. FİYAT" body={(row) => <span>₺{row.unitPrice.toLocaleString('tr-TR')}</span>} />
                    <Column field="lineTotal" header="TOPLAM" body={(row) => <span className="font-black">₺{row.lineTotal.toLocaleString('tr-TR')}</span>} />
                </DataTable>
            </div>

            {/* Alt Bilgiler */}
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">ÖDEME BİLGİLERİ</span>
                    <div className="space-y-2">
                        {selectedSaleDetail.payments && selectedSaleDetail.payments.length > 0 ? (
                            selectedSaleDetail.payments.map((p, idx) => (
                                <div key={idx} className="flex justify-between items-center text-xs">
                                    <span className="font-bold text-slate-600">{p.method}</span>
                                    <span className="font-black text-emerald-600">₺{p.amount.toLocaleString('tr-TR')}</span>
                                </div>
                            ))
                        ) : (
                            <p className="text-[10px] text-slate-400 italic">Ödeme kaydı bulunamadı.</p>
                        )}
                    </div>
                </div>

                <div className="bg-slate-900 text-white p-4 rounded-2xl shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-10 -mt-10 blur-xl"></div>
                    <div className="relative z-10 space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-[10px] font-bold text-slate-400 uppercase">Ara Toplam</span>
                            <span className="text-xs font-bold">₺{selectedSaleDetail.subTotal.toLocaleString('tr-TR')}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[10px] font-bold text-red-400 uppercase">İskonto</span>
                            <span className="text-xs font-bold text-red-400">-₺{selectedSaleDetail.discountTotal.toLocaleString('tr-TR')}</span>
                        </div>
                        <div className="pt-2 border-t border-white/10 flex justify-between items-center">
                            <span className="text-[10px] font-black text-blue-400 uppercase">GENEL TOPLAM</span>
                            <span className="text-lg font-black text-white">₺{selectedSaleDetail.grandTotal.toLocaleString('tr-TR')}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[10px] font-bold text-emerald-400 uppercase">TOPLAM ÖDENEN</span>
                            <span className="text-sm font-black text-emerald-400">₺{selectedSaleDetail.paidTotal.toLocaleString('tr-TR')}</span>
                        </div>
                    </div>
                </div>
            </div>

          </div>
        )}
      </Dialog>
      <Menu model={printMenuItems} popup ref={printMenuRef} appendTo={document.body} />
      <SaleReceipt ref={receiptRef} />
      <CustomerStatementReceipt ref={statementReceiptRef} />
    </div>
  );
}
